import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CostBreakdownResource } from '@lizrd/contracts';
import { getCostBreakdown } from '../lib/api';
import { Card } from './ui/card';
import { EstimatedTag } from './ui/EstimatedTag';
import { SkeletonCard } from './ui/skeleton';
import { CostTreemap } from './CostTreemap';
import { CostTrends } from './CostTrends';
import { consoleUrl, PROVIDER_ACCENT, typeLabel, usd } from '../lib/graph-visuals';
import { CostCategoryIcon, NodeTypeIcon } from './node-icons';
import { Lightbulb, Coins, Receipt } from 'lucide-react';

type CostView = 'breakdown' | 'treemap' | 'trend';

const share = (v: number, of: number) => (of > 0 ? (v / of) * 100 : 0);

/** A brand-consistent share bar beneath a row. */
function Bar({ pct, color }: { pct: number; color: string }) {
  return (
    <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-surface-2">
      <div
        className="h-full rounded-full"
        style={{ width: `${Math.max(2, Math.round(pct))}%`, backgroundColor: color }}
      />
    </div>
  );
}

/** The right-aligned "$X/mo" price tag. Billed-calibrated figures show plainly;
 * only figures still derived from list-price estimates carry the "Est." tag. */
function Price({
  cost,
  accent = false,
  billed = false,
}: {
  cost: number;
  accent?: boolean;
  billed?: boolean;
}) {
  return (
    <span className="ml-auto inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap">
      {!billed && <EstimatedTag compact />}
      <span>
        <strong className={accent ? 'font-display text-iris' : 'text-bone'}>{usd(cost)}</strong>
        <span className="text-[0.65rem] text-muted">/mo</span>
      </span>
    </span>
  );
}

/**
 * A native <details> that only mounts its body once opened, so a deep tree never
 * builds thousands of hidden DOM nodes up front.
 */
function LazyDetails({
  className,
  summary,
  children,
}: {
  className?: string;
  summary: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <details className={className} onToggle={(e) => setOpen(e.currentTarget.open)}>
      {summary}
      {open && children}
    </details>
  );
}

/** A console deep link for an aggregate resource (rebuilds the minimal ConsoleTarget). */
function resourceConsoleUrl(r: CostBreakdownResource): string | null {
  return consoleUrl({
    type: r.type,
    provider: r.provider,
    name: r.name,
    externalId: r.externalId,
    properties: r.region ? { region: r.region } : {},
  });
}

/**
 * Monthly Infrastructure Cost: collapsed to "Overall + total", expanding into cloud
 * providers → categories → the top resources by cost. Computed SERVER-SIDE over every
 * node in the domain (uncapped) via `getCostBreakdown`, so the totals are exact and the
 * "biggest spend" is the real biggest spend — not the alphabetical first page the old
 * client-side sum over the capped graph payload could show (scalability M2 → this).
 * In billed mode the breakdown reconciles to the billed headline: billed-calibrated
 * resources are itemized and the rest of the bill rolls into "Other services".
 */
export function CostBreakdown({ envId }: { envId: string }) {
  const [view, setView] = useState<CostView>('breakdown');
  const breakdown = useQuery({
    queryKey: ['cost-breakdown', envId],
    queryFn: () => getCostBreakdown(envId),
  });

  if (breakdown.isLoading) return <SkeletonCard lines={4} />;
  const data = breakdown.data;
  if (!data || data.totalMonthlyCost === 0) return null;

  const { providers, totalMonthlyCost: total, otherServicesTotal: other, resourceCount } = data;
  const hasBilled = data.provenance === 'billed';
  const allBilled = hasBilled || providers.every((p) => p.billed);
  const unbilledClouds = [...new Set(data.unbilledClouds.map((c) => c.provider))];

  return (
    <Card className="space-y-3 p-5 text-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold text-bone">Monthly Infrastructure Cost</h2>
          <p className="text-xs text-muted">
            {view === 'treemap'
              ? 'Sized by monthly cost — the biggest tile is your biggest spend.'
              : view === 'trend'
                ? 'How your spend moves over time — total, by cloud, and by resource type.'
                : 'Expand from overall spend down to each cloud resource.'}
          </p>
        </div>
        <div className="inline-flex overflow-hidden rounded-sm border border-border text-xs">
          {(['breakdown', 'treemap', 'trend'] as CostView[]).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-3 py-1.5 ${view === v ? 'bg-iris text-on-iris' : 'bg-surface text-muted hover:text-bone'}`}
            >
              {v === 'breakdown' ? 'Breakdown' : v === 'treemap' ? 'Tree map' : 'Trend'}
            </button>
          ))}
        </div>
      </div>

      {unbilledClouds.length > 0 && (
        <div className="rounded-sm border border-hairline bg-surface-2/40 px-3 py-2 text-xs text-muted">
          <Lightbulb
            aria-hidden
            className="mr-1 inline-block h-3.5 w-3.5 align-text-bottom text-iris"
          />
          <span className="text-bone">{unbilledClouds.join(' & ')}</span>{' '}
          {unbilledClouds.length === 1 ? 'is' : 'are'} connected but{' '}
          {unbilledClouds.length === 1 ? "isn't" : "aren't"} shown here yet — Lizrd doesn&rsquo;t
          have billed cost for {unbilledClouds.length === 1 ? 'this account' : 'these accounts'}.
          Connect a billing feed (for GCP, a BigQuery billing export in the integration settings) to
          include {unbilledClouds.length === 1 ? 'it' : 'them'} in your cost.
        </div>
      )}

      {view === 'treemap' && <CostTreemap envId={envId} />}
      {view === 'trend' && <CostTrends envId={envId} />}

      {/* Level 0 — Overall (collapsed by default). */}
      <LazyDetails
        className={`group rounded-md border border-hairline bg-surface-2/40 ${view === 'breakdown' ? '' : 'hidden'}`}
        summary={
          <summary className="flex cursor-pointer list-none items-center gap-2 p-3">
            <span className="inline-block w-3 shrink-0 text-muted transition-transform group-open:rotate-90">
              ▸
            </span>
            <Coins aria-hidden className="h-5 w-5 text-iris" />
            <span className="font-display text-base font-bold text-bone">Overall</span>
            <span className="text-[0.7rem] text-muted">
              {resourceCount} resource{resourceCount !== 1 ? 's' : ''} · {providers.length} cloud
              {providers.length !== 1 ? 's' : ''}
            </span>
            <Price cost={total} accent billed={allBilled} />
          </summary>
        }
      >
        {/* Level 1 — cloud providers. */}
        <div className="ml-4 mr-3 mb-3 space-y-1 border-l border-hairline pl-3">
          {providers.map((pdata) => {
            const accent = PROVIDER_ACCENT[pdata.provider] ?? '#8b93a7';
            return (
              <LazyDetails
                key={pdata.provider}
                className="group/prov rounded-sm"
                summary={
                  <summary className="flex cursor-pointer list-none items-center gap-2 py-1.5">
                    <span className="inline-block w-3 shrink-0 text-muted transition-transform group-open/prov:rotate-90">
                      ▸
                    </span>
                    <span
                      className="rounded-full px-2 py-0.5 text-[0.65rem] font-semibold"
                      style={{ backgroundColor: `${accent}22`, color: accent }}
                    >
                      {pdata.provider}
                    </span>
                    <Price cost={pdata.total} billed={pdata.billed} />
                  </summary>
                }
              >
                <div className="ml-2 border-l border-hairline pl-3">
                  <Bar pct={share(pdata.total, total)} color={accent} />

                  {/* Level 2 — categories. */}
                  <div className="mt-1 space-y-1">
                    {pdata.categories.map((cdata) => {
                      const hiddenCount = cdata.resourceCount - cdata.topResources.length;
                      return (
                        <LazyDetails
                          key={cdata.category}
                          className="group/cat"
                          summary={
                            <summary className="flex cursor-pointer list-none items-center gap-2 py-1">
                              <span className="inline-block w-3 shrink-0 text-muted transition-transform group-open/cat:rotate-90">
                                ▸
                              </span>
                              <CostCategoryIcon
                                category={cdata.category}
                                className="h-4 w-4 text-muted"
                              />
                              <span className="text-bone">{cdata.category}</span>
                              <span className="text-[0.65rem] text-muted">
                                {cdata.resourceCount}
                              </span>
                              <Price cost={cdata.total} billed={cdata.billed} />
                            </summary>
                          }
                        >
                          {/* Level 3 — top resources by cost. */}
                          <div className="ml-2 space-y-1 border-l border-hairline pl-3 pt-1">
                            {cdata.topResources.map((r) => {
                              const url = resourceConsoleUrl(r);
                              return (
                                <div key={r.nodeId} className="py-0.5">
                                  <div className="flex items-baseline gap-2">
                                    <span className="w-3 shrink-0" />
                                    <NodeTypeIcon type={r.type} className="h-4 w-4 text-muted" />
                                    <span className="truncate text-bone" title={r.name}>
                                      {r.name}
                                    </span>
                                    <span className="shrink-0 text-[0.65rem] text-muted">
                                      {typeLabel(r.type, r.provider)}
                                    </span>
                                    {url && (
                                      <a
                                        href={url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="shrink-0 text-[0.65rem] text-viridian hover:underline"
                                      >
                                        Details ↗
                                      </a>
                                    )}
                                    <Price cost={r.monthlyCost} billed={r.billed} />
                                  </div>
                                  <Bar pct={share(r.monthlyCost, cdata.total)} color={accent} />
                                </div>
                              );
                            })}
                            {hiddenCount > 0 && (
                              <p className="py-1 pl-5 text-[0.65rem] text-muted">
                                + {hiddenCount.toLocaleString()} more resource
                                {hiddenCount !== 1 ? 's' : ''} in this category (top{' '}
                                {cdata.topResources.length} by cost shown)
                              </p>
                            )}
                          </div>
                        </LazyDetails>
                      );
                    })}
                  </div>
                </div>
              </LazyDetails>
            );
          })}

          {/* The billed spend not itemized to a resource, so Overall == the headline:
              tax, data transfer, monitoring, and services we don't model individually. */}
          {other > 0.5 && (
            <LazyDetails
              className="group/other rounded-sm"
              summary={
                <summary className="flex cursor-pointer list-none items-center gap-2 py-1.5">
                  <span className="inline-block w-3 shrink-0 text-muted transition-transform group-open/other:rotate-90">
                    ▸
                  </span>
                  <Receipt aria-hidden className="h-4 w-4 shrink-0 text-muted" />
                  <span className="text-bone">Other services</span>
                  <span
                    className="text-[0.65rem] text-muted"
                    title="Billed cloud spend not itemized to a specific resource — tax, data transfer, monitoring, and platform services Lizrd doesn't model individually."
                  >
                    billed
                    {data.otherServices.length > 0
                      ? ` · ${data.otherServices.length} service${data.otherServices.length !== 1 ? 's' : ''}`
                      : ' · not itemized'}
                  </span>
                  <Price cost={other} billed />
                </summary>
              }
            >
              {data.otherServices.length > 0 && (
                <div className="ml-6 mr-3 mb-1 space-y-0.5 border-l border-hairline pl-3">
                  {data.otherServices.map((s) => (
                    <div key={s.service} className="flex items-center gap-2 py-1">
                      <span className="truncate text-muted">{s.service}</span>
                      <Price cost={s.amount} billed />
                    </div>
                  ))}
                  <p className="pt-1 text-[0.65rem] text-muted">
                    These are billed to your cloud account but aren&rsquo;t tied to a resource Lizrd
                    right-sizes — e.g. tax, data transfer, and usage-based platform services like
                    monitoring.
                  </p>
                </div>
              )}
            </LazyDetails>
          )}
        </div>
      </LazyDetails>
    </Card>
  );
}
